import { useLazyLoadQuery } from 'react-relay';
import BackgroundPresetsQuery, {
    productBackgroundSectionQuery
} from '../../pages/productConfiguration/steps/3-printAreas/resizablePrintArea/__generated__/productBackgroundSectionQuery.graphql';
import AssignedProductsQuery, { productsPrintTypesQuery } from '../../pages/printType/__generated__/productsPrintTypesQuery.graphql';
import { CustomErrorBoundary } from '../customErrorBoundary';
import { resolvePrintingMethodAssignments } from './agentPrintingMethodAssignments';
import { AgentPricingAssignments, PricingAssignmentTarget } from './AgentPricingAssignments';
import { isDraftRevisionApplied } from './agentDraftStatus';
import React, { FC, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from 'src/components/widgets/icon';
import { SkeletonBlock, SkeletonRow, SkeletonStack } from 'src/components/widgets/skeleton';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { stringFormat, T } from 'src/shared/helpers';
import config from 'src/shared/config';
import { Unit, getStartingUnit, units } from '../areaDesigner/helpers';
import { AdvancedSelect } from '../select';
import { FormControl } from '../formControl';
import Stack from '../stack/stack';
import { getResizableMaterialName } from '../../pages/productConfiguration/steps/3-printAreas/resizablePrintArea/resizableMaterialNames';
import { formatResizableMeasurement } from './agentResizableMeasurements';
import GridViewIcon from 'src/shared/material_icons/file/grid_view.svg';
import SingleSideIcon from 'src/shared/material_icons/image/crop_square.svg';
import ExpandMoreIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import styled from 'styled-components';
import { AgentProductSidePreview, AgentSideThumbnail } from './AgentProductSidePreview';
import {
    AgentDraft,
    AgentGeneratedImage,
    AgentProductPreviewActions,
    AgentResizableSetup,
    AgentResizableSurface,
    PricingCondition,
    PricingGroupSetup,
    PricingRule,
    PrintingMethodAssignment,
    PrintingMethodSetup
} from './types';

const Preview = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
    flex-direction: column;
    overflow-y: auto;
    /* Lets the side preview put image and details next to each other when there is room. */
    container: agent-preview / inline-size;
    /* The surrounding PreviewPanel paints the grey background; product sections are flat white bands on top of it. */
    background: transparent;
`;

// Printing method and pricing are still shown as cards, in their own padded area.
const Entities = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
`;

const Band = styled.div`
    flex-shrink: 0;
    padding: 14px 20px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    background: white;
`;

const Selectors = styled(Band)`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const ChipsWrap = styled.div`
    position: relative;
`;

// Off-screen copy of every chip, one row high: measures how many fit before the "+N" chip.
const MeasureLayer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-height: 26px;
    overflow: hidden;
    visibility: hidden;
    pointer-events: none;
`;

/** Shows one row of chips followed by a "+N" chip counting the rest; expands to show them all. */
const CollapsibleChips: FC<{ label: string; children: ReactNode }> = ({ label, children }) => {
    const [expanded, setExpanded] = useState(false);
    const [firstRowCount, setFirstRowCount] = useState(0);
    const measureRef = useRef<HTMLDivElement>(null);
    const items = React.Children.toArray(children);

    useLayoutEffect(() => {
        const element = measureRef.current;
        if (!element) return;
        const measure = () => {
            const chips = Array.from(element.children) as HTMLElement[];
            if (chips.length === 0) {
                setFirstRowCount(0);
                return;
            }
            const firstRowTop = chips[0].offsetTop;
            setFirstRowCount(chips.filter((chip) => chip.offsetTop <= firstRowTop + 1).length);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [children]);

    // Show every chip that fits; only when they spill past the first row keep the last slot for the "+N" chip.
    const allFit = firstRowCount >= items.length;
    const shown = expanded || firstRowCount === 0 || allFit ? items.length : Math.max(1, firstRowCount - 1);
    const hidden = items.length - shown;

    return (
        <ChipsWrap>
            <MeasureLayer ref={measureRef} aria-hidden='true'>
                {children}
            </MeasureLayer>
            <Chips role='tablist' aria-label={label}>
                {items.slice(0, shown)}
                {hidden > 0 && (
                    <FilterChip type='button' $selected={false} onClick={() => setExpanded(true)}>
                        +{hidden}
                    </FilterChip>
                )}
                {expanded && firstRowCount > 0 && items.length > firstRowCount && (
                    <FilterChip type='button' $selected={false} onClick={() => setExpanded(false)}>
                        {T._('Show less', 'Admin')}
                    </FilterChip>
                )}
            </Chips>
        </ChipsWrap>
    );
};

const SideRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const ViewToggle = styled.div`
    display: flex;
    flex-shrink: 0;
    gap: 4px;
`;

// Variant and side names read as filter chips (pill, filled), not as bordered buttons.
const FilterChip = styled.button<{ $selected: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 26px;
    padding: 0 12px;
    border: 0;
    border-radius: 999px;
    background: ${(props) => (props.$selected ? props.theme.app.primary_dark : '#eceff1')};
    color: ${(props) => (props.$selected ? 'white' : props.theme.app.textColor)};
    font-family: inherit;
    font-size: 12px;
    cursor: pointer;

    &:hover {
        background: ${(props) => (props.$selected ? props.theme.app.primary_dark : '#dfe4e7')};
    }

    &:focus-visible {
        outline: 2px solid #1769aa;
        outline-offset: 1px;
    }
`;

const SideGrid = styled.div`
    display: grid;
    flex-shrink: 0;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 12px;
    padding: 20px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
`;

const Chip = styled.button<{ $selected: boolean; $iconOnly?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 30px;
    padding: ${(props) => (props.$iconOnly ? '0 6px' : '0 12px')};
    border: 1px solid ${(props) => (props.$selected ? props.theme.ai.solid : props.theme.app.borderColor)};
    border-radius: 8px;
    background: ${(props) => (props.$selected ? '#faf5ff' : 'white')};
    color: ${(props) => (props.$selected ? props.theme.ai.solid : props.theme.app.textColor)};
    font-family: inherit;
    font-size: 13px;
    cursor: pointer;

    &:hover:not(:disabled) {
        border-color: ${(props) => (props.$selected ? props.theme.ai.solid : '#b0bec5')};
    }

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.ai.solid};
        outline-offset: 1px;
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-shrink: 0;
    min-height: 220px;
    align-items: center;
    justify-content: center;
    padding: 32px;
    border: 1px dashed #cfd8dc;
    border-radius: 12px;
    color: #607d8b;
    text-align: center;
`;

const ProductHeading = styled(Band)`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
`;

const ProductName = styled.h3`
    margin: 0;
    color: #263238;
    font-size: 16px;
    font-weight: 600;
`;

const ProductMeta = styled.p`
    margin: 2px 0 0;
    color: #78909c;
    font-size: 12px;
`;

// Keeps the name and the variants/prints summary on one line, to save a row of height.
const ProductTitleRow = styled.div`
    display: flex;
    min-width: 0;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 2px 8px;

    ${ProductMeta} {
        margin: 0;
    }
`;

// The left of the header: the product name and, under it, the revision pill.
const ProductTitleColumn = styled.div`
    display: flex;
    min-width: 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
`;

// The entity name as a chip by the revision; clicking it opens the explanation accordion below the header.
// Uses the notification-banner info blue, matching the explanation it reveals.
const EntityChip = styled.button<{ $open: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    height: 22px;
    padding: 0 6px 0 10px;
    border: 1px solid ${(props) => (props.$open ? '#0069d3' : props.theme.app.borderColor)};
    border-radius: 999px;
    background: ${(props) => (props.$open ? 'rgb(0 127 255 / 12%)' : 'white')};
    color: #0069d3;
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    text-transform: capitalize;
    cursor: pointer;

    ${Icon},
    ${Icon} svg {
        width: 14px;
        height: 14px;
        transition: transform 0.15s ease;
        transform: rotate(${(props) => (props.$open ? 180 : 0)}deg);
    }
`;

// Draft vs created, shown next to the entity chip: same pills as the rail cards.
const StatusChip = styled.span<{ $draft: boolean; $unused: boolean }>`
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8px;
    border-radius: 999px;
    background: ${(props) => (props.$unused ? '#eceff1' : props.$draft ? '#fff3e0' : '#e6f4ea')};
    color: ${(props) => (props.$unused ? '#78909c' : props.$draft ? '#b26a00' : '#1e7e34')};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
`;

// The entity explanation: a full-width blue strip across the preview, not a boxed banner.
const EntityExplain = styled.div`
    padding: 10px 20px;
    background: rgb(0 127 255 / 20%);
    color: #263238;
    font-size: 12px;
    line-height: 1.45;

    a {
        color: #0069d3;
        font-weight: 600;
    }
`;

const HeaderActions = styled.div<{ $standalone?: boolean }>`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;
    ${(props) => props.$standalone && 'justify-content: flex-end; padding: 12px 20px 0;'}
`;

const Revision = styled.span`
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8px;
    border-radius: 999px;
    background: #eceff1;
    color: #78909c;
    font-size: 11px;
    font-weight: 600;
`;

const Swatch = styled.span<{ $color: string }>`
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    border: 1px solid rgba(0, 0, 0, 0.14);
    border-radius: 50%;
    background: ${(props) => props.$color};
    /* Keeps dark colours visible on the dark selected chip. */
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.7);
`;

const Status = styled.span<{ $missing?: boolean }>`
    padding: 3px 6px;
    border-radius: 999px;
    background: ${(props) => (props.$missing ? '#fff3e0' : '#e8f5e9')};
    color: ${(props) => (props.$missing ? '#e65100' : '#2e7d32')};
    font-size: 9px;
    font-weight: 600;
`;

const EntityCard = styled.section`
    flex-shrink: 0;
    padding: 16px;
    border: 1px solid #dfe5e8;
    border-radius: 12px;
    background: white;
`;

const CapabilityList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 12px;
`;

const AdvancedSettings = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 14px;
`;

const AdvancedSetting = styled.details`
    border: 1px solid #eceff1;
    border-radius: 8px;
    background: #fafbfc;
    overflow: hidden;

    > summary {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 9px 10px;
        cursor: pointer;
        color: #455a64;
        font-size: 11px;
        font-weight: 600;
        list-style: none;
    }

    > summary::-webkit-details-marker {
        display: none;
    }
`;

const AdvancedSettingMeta = styled.span`
    margin-left: auto;
    color: #90a4ae;
    font-size: 10px;
    font-weight: 400;
`;

const AdvancedSettingBody = styled.div`
    padding: 0 10px 10px;
`;

const AssignmentDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    color: #455a64;
    font-size: 11px;

    > div {
        padding: 7px 8px;
        border-radius: 6px;
        background: white;
    }

    strong {
        font-weight: 600;
    }

    span {
        color: #78909c;
    }
`;

const PricingDescription = styled.p`
    margin: -4px 0 14px;
    color: #607d8b;
    font-size: 12px;
    line-height: 1.5;
`;

const PricingInfo = styled.div`
    margin-top: 14px;
    padding: 10px;
    border-radius: 8px;
    background: #f5f7fa;

    h5 {
        margin: 0 0 4px;
        color: #78909c;
        font-size: 10px;
        text-transform: uppercase;
    }

    p {
        margin: 0;
        color: #455a64;
        font-size: 11px;
        line-height: 1.45;
    }
`;

const Rules = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 14px;
`;

const Rule = styled.details`
    border: 1px solid #eceff1;
    border-radius: 9px;
    background: #fafbfc;
    overflow: hidden;

    > summary {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px;
        cursor: pointer;
        list-style: none;
    }

    > summary::-webkit-details-marker {
        display: none;
    }
`;

const RuleName = styled.span`
    min-width: 0;
    overflow: hidden;
    color: #37474f;
    font-size: 12px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const RuleMeta = styled.span`
    margin-left: auto;
    color: #78909c;
    font-size: 10px;
    white-space: nowrap;
`;

const RuleBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 10px 10px;
`;

const ConditionTable = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid #eceff1;
    border-radius: 7px;
    background: white;
`;

const ConditionRow = styled.div`
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 8px;
    align-items: center;
    padding: 10px;
    color: #455a64;
    font-size: 12px;

    &:not(:last-child) {
        border-bottom: 1px solid #eceff1;
    }
`;

const ConditionOrder = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #eceff1;
    color: #546e7a;
    font-size: 10px;
    font-weight: 600;
`;

const ConditionPrice = styled.span`
    color: #37474f;
    font-weight: 600;
    text-align: right;
`;

const Subsection = styled.div`
    h5 {
        margin: 0 0 6px;
        color: #78909c;
        font-size: 10px;
        text-transform: uppercase;
    }
`;

const TargetList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const Target = styled.span`
    padding: 5px 7px;
    border: 1px solid #eceff1;
    border-radius: 7px;
    background: white;
    color: #455a64;
    font-size: 10px;
`;

const CardSection = styled.section`
    & + & {
        margin-top: 14px;
    }
`;

const CardSectionTitle = styled.h5`
    margin: 0 0 6px;
    color: #78909c;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

const FieldGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 8px;
`;

const FieldCell = styled.div<{ $clickable?: boolean }>`
    display: block;
    width: 100%;
    min-width: 0;
    padding: 10px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: #f5f7fa;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};

    &:hover {
        border-color: ${(props) => (props.$clickable ? '#b0bec5' : 'transparent')};
    }
`;

const FieldLabel = styled.span`
    display: block;
    margin-bottom: 4px;
    color: #78909c;
    font-size: 10px;
    text-transform: uppercase;
`;

const FieldValue = styled.span`
    display: block;
    overflow: hidden;
    color: #37474f;
    font-size: 12px;
    font-weight: 600;
    text-overflow: ellipsis;
`;

const CapabilityGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 6px;
`;

// Both states are shown: an option left off is a decision the merchant must be able to check, not an absence.
const Capability = styled.span<{ $on: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 9px;
    border-radius: 8px;
    background: ${(props) => (props.$on ? '#e8f5e9' : '#f5f7fa')};
    color: ${(props) => (props.$on ? '#2e7d32' : '#90a4ae')};
    font-size: 11px;
    font-weight: 600;
`;

const CapabilityMark = styled.span`
    font-size: 12px;
    line-height: 1;
`;

const CardNotice = styled.div<{ $tone?: 'warning' | 'danger' }>`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    padding: 9px 10px;
    border-radius: 8px;
    background: ${(props) => (props.$tone === 'danger' ? '#fdecea' : '#fff3e0')};
    color: ${(props) => (props.$tone === 'danger' ? '#c62828' : '#e65100')};
    font-size: 11px;
    font-weight: 600;
`;

const CardNoticeAction = styled.button`
    flex: none;
    margin-left: auto;
    padding: 4px 9px;
    border: 1px solid #ffcc80;
    border-radius: 6px;
    background: white;
    color: #e65100;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: #fff8f2;
    }
`;

type CardField = { key: string; label: string; value: string; edit?: string };

/** The details of a card, one cell per field. A field the agent can change asks for it when clicked. */
const CardFields: FC<{ fields: CardField[]; onRequestEdit?: (message: string) => void }> = ({ fields, onRequestEdit }) => (
    <FieldGrid>
        {fields.map((field) => {
            const clickable = Boolean(onRequestEdit && field.edit);
            return (
                <FieldCell
                    key={field.key}
                    as={clickable ? 'button' : 'div'}
                    type={clickable ? 'button' : undefined}
                    $clickable={clickable}
                    title={clickable ? T._('Ask the agent to change this', 'Admin') : undefined}
                    onClick={clickable ? () => onRequestEdit?.(field.edit as string) : undefined}
                >
                    <FieldLabel>{field.label}</FieldLabel>
                    <FieldValue>{field.value}</FieldValue>
                </FieldCell>
            );
        })}
    </FieldGrid>
);

const PrintingMethodAssignments: FC<{ assignments: readonly PrintingMethodAssignment[] }> = ({ assignments: references }) => {
    // Refresh the ordinary assignment-page query: Agent writes use REST and may have
    // created a product since Relay last cached the catalogue.
    const catalogue = useLazyLoadQuery<productsPrintTypesQuery>(
        AssignedProductsQuery,
        {},
        {
            fetchPolicy: 'network-only',
            fetchKey: references.map((assignment) => assignment.productId).join(',')
        }
    );
    const assignments = resolvePrintingMethodAssignments(references, catalogue.modelsHierarchy);
    if (assignments.length === 0) return null;
    const sideAssignments = assignments.reduce((count, assignment) => count + (assignment.sides?.length ?? 0), 0);
    return (
        <CardSection>
            <CardSectionTitle>{T._('Assigned to', 'Admin')}</CardSectionTitle>
            <AdvancedSettings>
                <AdvancedSetting>
                    <summary>
                        {T._('Assignment details', 'Admin')}
                        <AdvancedSettingMeta>
                            {assignments.length} {T._('products', 'Admin')}
                            {sideAssignments > 0 ? ` · ${sideAssignments} ${T._('sides', 'Admin')}` : ''}
                        </AdvancedSettingMeta>
                    </summary>
                    <AdvancedSettingBody>
                        <AssignmentDetails>
                            {assignments.map((assignment) => (
                                <div key={assignment.productId}>
                                    <strong>{assignment.productName}</strong>
                                    {assignment.productCode && <span> · {assignment.productCode}</span>}
                                    {(assignment.allSides || assignment.sides.length > 0) && (
                                        <>
                                            <br />
                                            <span>{assignment.allSides ? T._('All sides', 'Admin') : assignment.sides.join(', ')}</span>
                                        </>
                                    )}
                                </div>
                            ))}
                        </AssignmentDetails>
                    </AdvancedSettingBody>
                </AdvancedSetting>
            </AdvancedSettings>
        </CardSection>
    );
};

const PrintingMethodPreview: FC<{ method: PrintingMethodSetup; onRequestEdit?: (message: string) => void }> = ({ method, onRequestEdit }) => {
    const assignments = method.assignments ?? [];

    // The advanced payloads mirror the print type model (see the printType page), so their own fields can be read back
    // and summarised: "Configured" said nothing about what the agent actually decided.
    const asRecord = (value: unknown): Record<string, unknown> | null =>
        value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
    const restrictions = asRecord(method.restrictions);
    const outputSettings = asRecord(method.outputSettings);
    const backgroundRemover = asRecord(method.backgroundRemoverSettings);
    const patternSettings = asRecord(method.patternSetting ?? method.patternSettings);
    const numberOf = (source: Record<string, unknown> | null, key: string) => (typeof source?.[key] === 'number' ? (source[key] as number) : null);
    const isOn = (source: Record<string, unknown> | null, key: string) => source?.[key] === true;

    const notSet = T._('Not set', 'Admin');
    const noLimit = T._('No limit', 'Admin');

    const effectTypeLabel = (value: unknown) => {
        const effect = asRecord(value);
        if (!effect) return T._('Configured', 'Admin');
        const rawType = effect.type ?? effect.imageEffectType;
        const numericType = typeof rawType === 'number' ? rawType : Number(rawType);
        if (numericType === 1) return T._('Engraving', 'Admin');
        if (numericType === 2) return T._('Print on Wood', 'Admin');
        if (numericType === 3) return T._('Pixelation', 'Admin');
        return typeof effect.imageEffectTypeName === 'string' ? effect.imageEffectTypeName : T._('Configured', 'Admin');
    };

    const outputLayout = (() => {
        const columns = numberOf(outputSettings, 'columns');
        const rows = numberOf(outputSettings, 'rows');
        const width = numberOf(outputSettings, 'width');
        const height = numberOf(outputSettings, 'height');
        if (columns != null && rows != null) return `${columns} × ${rows}${width != null && height != null ? ` · ${width} × ${height}` : ''}`;
        if (width != null && height != null) return `${width} × ${height}`;
        return null;
    })();

    const printReadyFields: CardField[] = [
        { key: 'dpi', label: T._('DPI', 'Admin'), value: method.dpi ? `${method.dpi}` : notSet, edit: T._('Change the DPI of this printing method.', 'Admin') },
        {
            key: 'format',
            label: T._('Standard output format', 'Admin'),
            value: method.outputFileFormats?.map((format) => format.toUpperCase()).join(', ') || notSet,
            edit: T._('Change the output formats of this printing method.', 'Admin')
        },
        {
            key: 'outputType',
            label: T._('Print files', 'Admin'),
            value: method.outputType === 'filePerColor' ? T._('One file per color', 'Admin') : T._('One file per print area', 'Admin'),
            edit: T._('Change how the print-ready files are split for this printing method.', 'Admin')
        }
    ];
    if (outputLayout) printReadyFields.push({ key: 'layout', label: T._('Output layout', 'Admin'), value: outputLayout });
    if (method.colorProfileFileName || method.colorProfileFileUrl)
        printReadyFields.push({
            key: 'icc',
            label: T._('Color profile', 'Admin'),
            value: method.colorProfileFileName || (method.colorProfileFileUrl as string)
        });

    const colorFields: CardField[] = [
        {
            key: 'maxColors',
            label: T._('Max number of colors', 'Admin'),
            value: method.maxColors != null ? `${method.maxColors}` : noLimit,
            edit: T._('Change the color limit of this printing method.', 'Admin')
        },
        {
            key: 'pantone',
            label: T._('Pantone/CMYK mode', 'Admin'),
            value: method.pantoneEnabled ? T._('Enabled', 'Admin') : T._('Disabled', 'Admin'),
            edit: T._('Change the Pantone/CMYK mode of this printing method.', 'Admin')
        },
        {
            key: 'palette',
            label: T._('Palette', 'Admin'),
            value: method.paletteColors?.length
                ? `${method.paletteColors.length} ${T._('colors', 'Admin')}`
                : method.paletteID != null
                  ? T._('Palette selected', 'Admin')
                  : T._('None', 'Admin'),
            edit: T._('Change the color palette of this printing method.', 'Admin')
        },
        {
            key: 'replaceColors',
            label: T._('Replace colors', 'Admin'),
            value: method.rgbConversions?.length
                ? method.rgbConversions.map((rule) => `${rule.rgbFrom} → ${rule.rgbTo}`).join(', ')
                : method.rgbConversionListID != null
                  ? T._('List selected', 'Admin')
                  : T._('Disabled', 'Admin'),
            edit: T._('Change the replace colors of this printing method.', 'Admin')
        }
    ];

    // What the customer can add in the editor, on and off alike.
    const capabilities: Array<{ key: string; label: string; on: boolean }> = [
        { key: 'text', label: T._('Text', 'Admin'), on: Boolean(method.allowText ?? isOn(restrictions, 'canAddText')) },
        { key: 'uploads', label: T._('Image uploads', 'Admin'), on: Boolean(method.allowUploads) },
        { key: 'gallery', label: T._('Images & Clipart gallery', 'Admin'), on: Boolean(method.allowGallery) },
        {
            key: 'patterns',
            label: T._('Repeated patterns', 'Admin'),
            on: Boolean(patternSettings && (isOn(patternSettings, 'isImageEnabled') || isOn(patternSettings, 'isTextEnabled')))
        },
        {
            key: 'backgroundRemover',
            label: T._('AI Background Remover', 'Admin'),
            on: Boolean(backgroundRemover && (isOn(backgroundRemover, 'isUploadEnabled') || isOn(backgroundRemover, 'isEditEnabled')))
        },
        { key: 'vectorizer', label: T._('AI Vectorizer', 'Admin'), on: isOn(restrictions, 'enableVectorize') }
    ];

    const textStyles = [
        isOn(restrictions, 'canUseTextArt') && T._('TextArt', 'Admin'),
        isOn(restrictions, 'textBox') && T._('Textbox', 'Admin'),
        isOn(restrictions, 'curvedText') && T._('Curved text', 'Admin'),
        isOn(restrictions, 'allowBold') && T._('Bold', 'Admin'),
        isOn(restrictions, 'allowItalic') && T._('Italic', 'Admin'),
        isOn(restrictions, 'uppercase') && T._('Uppercase', 'Admin')
    ].filter(Boolean) as string[];

    const fontSize = (() => {
        const min = numberOf(restrictions, 'minFontSize');
        const max = numberOf(restrictions, 'maxFontSize');
        if (min != null && max != null) return `${min}–${max}`;
        if (max != null) return `≤ ${max}`;
        if (min != null) return `≥ ${min}`;
        return null;
    })();

    const maxTexts = numberOf(restrictions, 'maxNrTextElements');
    const maxImages = numberOf(restrictions, 'maxNrImages');
    const textFields: CardField[] = [
        {
            key: 'fonts',
            label: T._('Fonts', 'Admin'),
            value: method.fonts?.length
                ? `${method.fonts.length} ${T._('selected', 'Admin')}`
                : isOn(restrictions, 'useCustomFonts')
                  ? T._('Custom selection', 'Admin')
                  : T._('All fonts', 'Admin'),
            edit: T._('Change the fonts of this printing method.', 'Admin')
        },
        {
            key: 'maxTexts',
            label: T._('Max number of text elements', 'Admin'),
            value: maxTexts != null ? `${maxTexts}` : noLimit,
            edit: T._('Change the maximum number of text elements of this printing method.', 'Admin')
        },
        {
            key: 'maxImages',
            label: T._('Max number of images', 'Admin'),
            value: maxImages != null ? `${maxImages}` : noLimit,
            edit: T._('Change the maximum number of images of this printing method.', 'Admin')
        }
    ];
    if (fontSize) textFields.push({ key: 'fontSize', label: T._('Font size', 'Admin'), value: fontSize });
    if (textStyles.length > 0) textFields.push({ key: 'textStyles', label: T._('Text options', 'Admin'), value: textStyles.join(', ') });

    const effectFields: CardField[] = [];
    if (method.previewDesignEffect)
        effectFields.push({ key: 'previewEffect', label: T._('Preview design effect', 'Admin'), value: effectTypeLabel(method.previewDesignEffect) });
    if (method.outputDesignEffect)
        effectFields.push({ key: 'outputEffect', label: T._('Output design effect', 'Admin'), value: effectTypeLabel(method.outputDesignEffect) });
    if (method.splitColorFiles || method.layeredPdf)
        effectFields.push({
            key: 'outputExtras',
            label: T._('Special output', 'Admin'),
            value: [method.splitColorFiles && T._('Separated colors', 'Admin'), method.layeredPdf && T._('Layered PDF', 'Admin')].filter(Boolean).join(' · ')
        });

    return (
        <EntityCard>
            {/* The name and status live in the shared preview header above; the card starts straight at its details. */}
            {assignments.length === 0 && (
                <CardNotice>
                    <span>{T._('Not assigned to any product yet: until you assign it, nothing is printed with it.', 'Admin')}</span>
                    {onRequestEdit && (
                        <CardNoticeAction type='button' onClick={() => onRequestEdit(T._('Assign this printing method to a product.', 'Admin'))}>
                            {T._('Assign', 'Admin')}
                        </CardNoticeAction>
                    )}
                </CardNotice>
            )}

            <CardSection>
                <CardSectionTitle>{T._('Print-ready files', 'Admin')}</CardSectionTitle>
                <CardFields fields={printReadyFields} onRequestEdit={onRequestEdit} />
            </CardSection>

            <CardSection>
                <CardSectionTitle>{T._('Colors', 'Admin')}</CardSectionTitle>
                <CardFields fields={colorFields} onRequestEdit={onRequestEdit} />
            </CardSection>

            <CardSection>
                <CardSectionTitle>{T._('What customers can add', 'Admin')}</CardSectionTitle>
                <CapabilityGrid>
                    {capabilities.map((capability) => (
                        <Capability key={capability.key} $on={capability.on}>
                            <CapabilityMark aria-hidden='true'>{capability.on ? '✓' : '–'}</CapabilityMark>
                            {capability.label}
                        </Capability>
                    ))}
                </CapabilityGrid>
            </CardSection>

            <CardSection>
                <CardSectionTitle>{T._('Text', 'Admin')}</CardSectionTitle>
                <CardFields fields={textFields} onRequestEdit={onRequestEdit} />
            </CardSection>

            {effectFields.length > 0 && (
                <CardSection>
                    <CardSectionTitle>{T._('Effects', 'Admin')}</CardSectionTitle>
                    <CardFields fields={effectFields} onRequestEdit={onRequestEdit} />
                </CardSection>
            )}

            {assignments.length > 0 && (
                <CustomErrorBoundary
                    resetKey={JSON.stringify(assignments)}
                    render={() => <CardNotice>{T._('Assigned products could not be loaded.', 'Admin')}</CardNotice>}
                >
                    <React.Suspense fallback={<SkeletonBlock $width='100%' $height='44px' />}>
                        <PrintingMethodAssignments assignments={assignments} />
                    </React.Suspense>
                </CustomErrorBoundary>
            )}
        </EntityCard>
    );
};

const priceStructureLabel = (value?: string) => {
    switch (value) {
        case 'finalPrice':
            return T._('Final price', 'Admin');
        case 'separatedPrices':
            return T._('Separated prices', 'Admin');
        case 'customizationPrice':
            return T._('Customization price', 'Admin');
        default:
            return value || T._('Not set', 'Admin');
    }
};

const assignmentLabel = (value?: string) => {
    switch (value) {
        case 'products':
            return T._('Products', 'Admin');
        case 'printingMethods':
            return T._('Printing methods', 'Admin');
        default:
            return value || T._('None', 'Admin');
    }
};

const priceTypeLabel = (value?: string) => {
    if (value === 'percentage') return T._('Percentage', 'Admin');
    if (value === 'fixed') return T._('Fixed amount', 'Admin');
    return value || T._('Not set', 'Admin');
};

const formatNumber = (value?: number) => (value == null ? T._('Not set', 'Admin') : value.toLocaleString());

// What the rule measures. The ids are the pricing engine's own property ids, the same ones the pricing page builds its
// presets with (see useRulePresets and the discovery seed): without them a rule reads as "Rule 1" and says nothing.
const PRICING_PROPERTIES: Record<number, { label: string; unit: string }> = {
    4: { label: T._('Text length', 'Admin'), unit: T._('characters', 'Admin') },
    5: { label: T._('Element size', 'Admin'), unit: 'cm²' },
    6: { label: T._('Number of lines', 'Admin'), unit: T._('lines', 'Admin') },
    9: { label: T._('Number of elements', 'Admin'), unit: T._('elements', 'Admin') },
    10: { label: T._('Number of colors', 'Admin'), unit: T._('colors', 'Admin') },
    11: { label: T._('Print area size', 'Admin'), unit: 'cm²' },
    12: { label: T._('Total text length', 'Admin'), unit: T._('characters', 'Admin') },
    13: { label: T._('Customized sides', 'Admin'), unit: T._('sides', 'Admin') },
    14: { label: T._('Design size', 'Admin'), unit: 'cm²' },
    15: { label: T._('Price per element', 'Admin'), unit: T._('elements', 'Admin') }
};

const PRICING_ELEMENTS: Record<number, string> = {
    1: T._('all elements', 'Admin'),
    8: T._('text elements', 'Admin'),
    11: T._('uploaded images', 'Admin')
};

const rulePropertyLabel = (rule: PricingRule) => {
    const property = rule.propertyId != null ? PRICING_PROPERTIES[rule.propertyId] : undefined;
    const scope = rule.elementId != null ? PRICING_ELEMENTS[rule.elementId] : undefined;
    if (!property) return rule.elementTitle || T._('Custom property', 'Admin');
    return scope ? `${property.label} · ${scope}` : property.label;
};

const ruleUnit = (rule: PricingRule) => (rule.propertyId != null ? PRICING_PROPERTIES[rule.propertyId]?.unit : undefined);

// The condition read as the sentence the merchant would say: "up to 50 cm² → +5".
const conditionWhen = (condition: PricingCondition, rule: PricingRule) => {
    const unit = ruleUnit(rule);
    const withUnit = (value: number) => `${formatNumber(value)}${unit ? ` ${unit}` : ''}`;
    const values = condition.formula?.values ?? [];
    const operator = condition.formula?.operator;
    const quantity = condition.quantity ?? 0;
    const fromQuantity = quantity > 1 ? ` · ${stringFormat(T._('from {0} pieces', 'Admin'), formatNumber(quantity))}` : '';

    const measure = (() => {
        if (values.length === 0) return T._('Always', 'Admin');
        if (operator === 'between' && values.length > 1) return stringFormat(T._('Between {0} and {1}', 'Admin'), withUnit(values[0]), withUnit(values[1]));
        if (operator === '<=') return stringFormat(T._('Up to {0}', 'Admin'), withUnit(values[0]));
        if (operator === '<') return stringFormat(T._('Under {0}', 'Admin'), withUnit(values[0]));
        if (operator === '>=') return values[0] === 0 ? T._('Always', 'Admin') : stringFormat(T._('From {0}', 'Admin'), withUnit(values[0]));
        if (operator === '>') return stringFormat(T._('Over {0}', 'Admin'), withUnit(values[0]));
        if (operator === '=') return stringFormat(T._('Exactly {0}', 'Admin'), withUnit(values[0]));
        if (operator === '!=') return stringFormat(T._('Other than {0}', 'Admin'), withUnit(values[0]));
        return `${operator ?? ''} ${values.map((value) => withUnit(value)).join(', ')}`.trim();
    })();

    return `${measure}${fromQuantity}`;
};

const conditionPrice = (condition: PricingCondition, rule: PricingRule) => {
    const value = condition.priceToAdd;
    if (value == null) return T._('Not set', 'Admin');
    const amount = `${formatNumber(Math.abs(value))}${rule.priceType === 'percentage' ? '%' : ''}`;
    if (value === 0) return T._('No charge', 'Admin');
    return `${value < 0 ? '−' : '+'} ${amount}`;
};

const targetLabel = (target: NonNullable<PricingRule['targets']>[number]) => {
    const parts = [target.color, target.side, target.area].filter((value): value is string => Boolean(value));
    return parts.length > 0 ? parts.join(' · ') : T._('All areas, all sides', 'Admin');
};

// Conditions that cannot both be read as a complete price table: the same test twice, or a value that no condition
// covers (for example "< 50" next to "> 50", which leaves 50 unpriced). The pricing page refuses duplicates on save,
// so saying it here saves a round trip.
const ruleWarnings = (rule: PricingRule): string[] => {
    const conditions = rule.conditions ?? [];
    const warnings: string[] = [];
    const signatures = new Set<string>();
    conditions.forEach((condition) => {
        const signature = `${condition.quantity ?? 0}|${condition.formula?.operator ?? ''}|${(condition.formula?.values ?? []).join(',')}`;
        if (signatures.has(signature)) warnings.push(T._('The same condition is repeated.', 'Admin'));
        signatures.add(signature);
    });
    conditions.forEach((condition) => {
        const values = condition.formula?.values ?? [];
        if (condition.formula?.operator !== '<' || values.length === 0) return;
        const boundary = values[0];
        const covered = conditions.some((other) => {
            const otherValues = other.formula?.values ?? [];
            if (otherValues.length === 0 || other.quantity !== condition.quantity) return false;
            const operator = other.formula?.operator;
            if (operator === '=' || operator === '<=' || operator === '>=') return otherValues[0] === boundary;
            if (operator === 'between') return otherValues.length > 1 && otherValues[0] <= boundary && otherValues[1] >= boundary;
            return false;
        });
        if (!covered) warnings.push(stringFormat(T._('No price is set for exactly {0}.', 'Admin'), formatNumber(boundary)));
    });
    return [...new Set(warnings)];
};

const PricingRulePreview: FC<{ rule: PricingRule; index: number }> = ({ rule, index }) => {
    const conditions = [...(rule.conditions ?? [])].sort((left, right) => (left.position ?? 0) - (right.position ?? 0));
    const targets = rule.targets ?? [];
    const warnings = ruleWarnings(rule);
    const allMatch = rule.matchMode === 'allMatch';

    return (
        <Rule open={index === 0}>
            <summary>
                <RuleName>{rulePropertyLabel(rule)}</RuleName>
                <RuleMeta>
                    {conditions.length} {conditions.length === 1 ? T._('condition', 'Admin') : T._('conditions', 'Admin')}
                </RuleMeta>
            </summary>
            <RuleBody>
                <CapabilityList>
                    <Status>{priceTypeLabel(rule.priceType)}</Status>
                    {rule.isSetupPrice && <Status>{T._('Setup price', 'Admin')}</Status>}
                    {rule.simplified && <Status>{T._('Incremental pricing', 'Admin')}</Status>}
                </CapabilityList>
                <Subsection>
                    <h5>{T._('Conditions', 'Admin')}</h5>
                    {/* How the conditions are read decides the final price, so it is written out, not left to a chip. */}
                    <ProductMeta>
                        {allMatch
                            ? T._('Every matching condition is added up.', 'Admin')
                            : T._('Only the first matching condition is applied, in this order.', 'Admin')}
                    </ProductMeta>
                    {conditions.length > 0 ? (
                        <ConditionTable>
                            {conditions.map((condition, conditionIndex) => (
                                <ConditionRow key={condition.conditionId || `${condition.position ?? 0}-${conditionIndex}`}>
                                    <ConditionOrder>{allMatch ? '·' : conditionIndex + 1}</ConditionOrder>
                                    <span>{conditionWhen(condition, rule)}</span>
                                    <ConditionPrice>{conditionPrice(condition, rule)}</ConditionPrice>
                                </ConditionRow>
                            ))}
                        </ConditionTable>
                    ) : (
                        <ProductMeta>{T._('No conditions', 'Admin')}</ProductMeta>
                    )}
                </Subsection>
                {warnings.map((warning) => (
                    <CardNotice key={warning} $tone='danger'>
                        <span>{warning}</span>
                    </CardNotice>
                ))}
                <Subsection>
                    <h5>{T._('Targets', 'Admin')}</h5>
                    <TargetList>
                        {targets.length > 0 ? (
                            targets.map((target, targetIndex) => <Target key={target.targetId || targetIndex}>{targetLabel(target)}</Target>)
                        ) : (
                            <Target>{T._('All areas, all sides', 'Admin')}</Target>
                        )}
                    </TargetList>
                </Subsection>
            </RuleBody>
        </Rule>
    );
};

const PricingAssignmentDetails: FC<{
    targets: readonly PricingAssignmentTarget[];
    targetType?: string;
    onRequestEdit?: (message: string) => void;
}> = ({ targets, targetType, onRequestEdit }) => {
    if (targets.length === 0) return null;
    return (
        <CardSection>
            <CardSectionTitle>{T._('Assigned to', 'Admin')}</CardSectionTitle>
            <AdvancedSettings>
                <AdvancedSetting>
                    <summary>
                        {T._('Assignment details', 'Admin')}
                        <AdvancedSettingMeta>
                            {assignmentLabel(targetType)} · {targets.length}
                        </AdvancedSettingMeta>
                    </summary>
                    <AdvancedSettingBody>
                        <AssignmentDetails>
                            {targets.map((target) => (
                                <div key={target.id}>
                                    <strong>{target.name}</strong>
                                    {target.code && <span> · {target.code}</span>}
                                </div>
                            ))}
                        </AssignmentDetails>
                    </AdvancedSettingBody>
                </AdvancedSetting>
            </AdvancedSettings>
            {onRequestEdit && (
                <CardNoticeAction type='button' onClick={() => onRequestEdit(T._('Change what this pricing is assigned to.', 'Admin'))}>
                    {T._('Edit', 'Admin')}
                </CardNoticeAction>
            )}
        </CardSection>
    );
};

const PricingPreview: FC<{ pricing: PricingGroupSetup; onRequestEdit?: (message: string) => void }> = ({ pricing, onRequestEdit }) => {
    const rules = pricing.rules ?? [];
    const assignments = pricing.assignments;
    const targetIds = assignments?.targetIds ?? [];
    const assigned = targetIds.length > 0 && assignments?.targetType !== 'none';

    const generalFields: CardField[] = [
        {
            key: 'priceStructure',
            label: T._('Price structure', 'Admin'),
            value: priceStructureLabel(pricing.priceStructure),
            edit: T._('Change the price structure of this pricing.', 'Admin')
        },
        { key: 'rules', label: T._('Rules', 'Admin'), value: `${rules.length}` },
        ...(!assigned
            ? [
                  {
                      key: 'assignedTo',
                      label: T._('Assigned to', 'Admin'),
                      value: T._('Nothing yet', 'Admin'),
                      edit: T._('Change what this pricing is assigned to.', 'Admin')
                  }
              ]
            : []),
        // Shown whether on or off: an option left off is a decision, and the merchant must be able to check it.
        {
            key: 'detailedPrice',
            label: T._('Detailed price', 'Admin'),
            value: pricing.showDetailedPrice ? T._('Enabled', 'Admin') : T._('Disabled', 'Admin'),
            edit: T._('Change the detailed price option of this pricing.', 'Admin')
        },
        {
            key: 'priceDescription',
            label: T._('Price description', 'Admin'),
            value: pricing.priceDescriptionEnabled ? T._('Enabled', 'Admin') : T._('Disabled', 'Admin'),
            edit: T._('Change the price description option of this pricing.', 'Admin')
        },
        {
            key: 'quantityAsUnit',
            label: T._('Quantity as unit', 'Admin'),
            value: pricing.quantityAsUnit ? T._('Enabled', 'Admin') : T._('Disabled', 'Admin'),
            edit: T._('Change the quantity as unit option of this pricing.', 'Admin')
        }
    ];

    return (
        <EntityCard>
            {/* The name and status live in the shared preview header above; the card starts with the description. */}
            {pricing.description && <PricingDescription>{pricing.description}</PricingDescription>}
            {!assigned && (
                <CardNotice $tone='warning'>
                    <span>{T._('Not assigned to any product or printing method yet: until you assign it, no customer pays it.', 'Admin')}</span>
                    {onRequestEdit && (
                        <CardNoticeAction type='button' onClick={() => onRequestEdit(T._('Assign this pricing to a product.', 'Admin'))}>
                            {T._('Assign', 'Admin')}
                        </CardNoticeAction>
                    )}
                </CardNotice>
            )}
            <CardSection>
                <CardSectionTitle>{T._('General', 'Admin')}</CardSectionTitle>
                <CardFields fields={generalFields} onRequestEdit={onRequestEdit} />
            </CardSection>
            {assigned && assignments && (
                <CustomErrorBoundary
                    resetKey={JSON.stringify(assignments)}
                    render={() => <CardNotice>{T._('Assigned products or printing methods could not be loaded.', 'Admin')}</CardNotice>}
                >
                    <React.Suspense fallback={<SkeletonBlock $width='100%' $height='44px' />}>
                        <AgentPricingAssignments assignments={assignments}>
                            {(targets) => <PricingAssignmentDetails targets={targets} targetType={assignments.targetType} onRequestEdit={onRequestEdit} />}
                        </AgentPricingAssignments>
                    </React.Suspense>
                </CustomErrorBoundary>
            )}
            {pricing.infoPricingText && (
                <PricingInfo>
                    <h5>{T._('Pricing information', 'Admin')}</h5>
                    <p>{pricing.infoPricingText}</p>
                </PricingInfo>
            )}
            <CardSection>
                <CardSectionTitle>{T._('Rules', 'Admin')}</CardSectionTitle>
                {rules.length > 0 ? (
                    <Rules>
                        {rules.map((rule, index) => (
                            <PricingRulePreview key={rule.ruleId || index} rule={rule} index={index} />
                        ))}
                    </Rules>
                ) : (
                    <EmptyState>{T._('No pricing rules have been added yet.', 'Admin')}</EmptyState>
                )}
            </CardSection>
        </EntityCard>
    );
};

const SkeletonLayout = styled.div`
    display: flex;
    flex-shrink: 0;
    flex-direction: column;

    @container agent-preview (min-width: 640px) {
        display: grid;
        grid-template-columns: minmax(0, 3fr) minmax(260px, 2fr);
        border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    }
`;

const SkeletonStage = styled.div`
    padding: 20px;
`;

const SkeletonDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 16px 20px;
    border-left: 1px solid ${(props) => props.theme.app.borderColor};
    background: white;
`;

/** Printing method and pricing skeleton: mirrors their card (heading, details grid, list of rules or options). */
const EntitySkeleton: FC = () => (
    <Preview role='status' aria-busy='true' aria-label={T._('Building the artifact…', 'Admin')}>
        <Entities>
            <EntityCard>
                <SkeletonStack $gap='14px'>
                    <SkeletonRow $justify='space-between'>
                        <SkeletonStack $gap='6px'>
                            <SkeletonBlock $width='180px' $height='16px' />
                            <SkeletonBlock $width='120px' $height='11px' />
                        </SkeletonStack>
                        <SkeletonBlock $width='48px' $height='20px' $radius='6px' />
                    </SkeletonRow>
                    {['90px', '60px', '120px', '40px'].map((width) => (
                        <SkeletonRow key={width} $justify='space-between'>
                            <SkeletonBlock $width='110px' $height='13px' />
                            <SkeletonBlock $width={width} $height='13px' />
                        </SkeletonRow>
                    ))}
                    {['100%', '100%', '70%'].map((width, index) => (
                        <SkeletonBlock key={index} $width={width} $height='44px' $radius='8px' />
                    ))}
                </SkeletonStack>
            </EntityCard>
        </Entities>
    </Preview>
);

/** Shown while the agent is creating an artifact, shaped like the artifact being created. */
export const AgentArtifactSkeleton: FC<{ kind: AgentArtifactKind }> = ({ kind }) => (kind === 'product' ? <ProductSkeleton /> : <EntitySkeleton />);

/** Mirrors the product artifact (heading, chips, image, details). */
const ProductSkeleton: FC = () => (
    <Preview role='status' aria-busy='true' aria-label={T._('Building the artifact…', 'Admin')}>
        <ProductHeading>
            <SkeletonStack $gap='6px'>
                <SkeletonBlock $width='160px' $height='16px' />
                <SkeletonBlock $width='120px' $height='11px' />
            </SkeletonStack>
            <SkeletonBlock $width='48px' $height='20px' $radius='6px' />
        </ProductHeading>
        <Selectors>
            <SkeletonRow $gap='8px'>
                {['72px', '64px', '80px'].map((width) => (
                    <SkeletonBlock key={width} $width={width} $height='26px' $radius='999px' />
                ))}
            </SkeletonRow>
        </Selectors>
        <SkeletonLayout>
            <SkeletonStage>
                <SkeletonBlock $height='320px' $radius='12px' />
            </SkeletonStage>
            <SkeletonDetails>
                <SkeletonBlock $width='90px' $height='11px' />
                <SkeletonRow $justify='space-between'>
                    <SkeletonBlock $width='140px' $height='13px' />
                    <SkeletonBlock $width='70px' $height='13px' />
                </SkeletonRow>
                <SkeletonRow $gap='8px'>
                    <SkeletonBlock $width='84px' $height='26px' $radius='6px' />
                    <SkeletonBlock $width='84px' $height='26px' $radius='6px' />
                </SkeletonRow>
                <SkeletonBlock $width='90px' $height='11px' $margin='12px 0 0' />
                {['60px', '90px', '40px'].map((width) => (
                    <SkeletonRow key={width} $justify='space-between'>
                        <SkeletonBlock $width='50px' $height='13px' />
                        <SkeletonBlock $width={width} $height='13px' />
                    </SkeletonRow>
                ))}
            </SkeletonDetails>
        </SkeletonLayout>
    </Preview>
);

export type AgentArtifactKind = 'product' | 'printingMethod' | 'pricing';

export interface AgentArtifact {
    kind: AgentArtifactKind;
    label: string;
    /** `draft` is still pending (not yet applied); `created` has been persisted to the backend. */
    status: 'draft' | 'created' | 'pending';
}

const ARTIFACT_KINDS: AgentArtifactKind[] = ['product', 'printingMethod', 'pricing'];

export const hasPendingArtifactApproval = (draft: AgentDraft, kind: AgentArtifactKind): boolean => {
    const item = draft.drafts?.find((entry) => entry.kind === kind);
    const document = item?.document as { _meta?: { approvalOffer?: unknown } } | null | undefined;
    return Boolean(document?._meta?.approvalOffer);
};

/** Each entity the conversation works on is its own artifact, in the order its drafts were created. */
export const getAgentArtifacts = (draft: AgentDraft): AgentArtifact[] => {
    const document = draft.document;
    // Always the tool first, then the name the agent gave it: "Printing Method - Text only".
    const label = (tool: string, name?: string | null) => (name?.trim() ? `${tool} - ${name.trim()}` : tool);
    const printingMethod = document?.printingMethodSetup?.printingMethod;
    const pricingGroup = document?.pricingSetup?.pricingGroup;
    const labels: Record<AgentArtifactKind, string | null> = {
        product: document?.product ? label(T._('Customizable Product', 'Admin'), document.product.name) : null,
        printingMethod: printingMethod ? label(T._('Printing Method', 'Admin'), printingMethod.name) : null,
        pricing: pricingGroup ? label(T._('Pricing Rules', 'Admin'), pricingGroup.name) : null
    };
    // A catalogue ID identifies the target of an edit; only an applied draft revision proves it was saved.
    const created = (kind: AgentArtifactKind) => {
        const item = draft.drafts?.find((entry) => entry.kind === kind);
        return isDraftRevisionApplied(item);
    };
    const draftOrder = (draft.drafts ?? [])
        .map((item) => item.kind)
        .filter((kind): kind is AgentArtifactKind => ARTIFACT_KINDS.includes(kind as AgentArtifactKind));
    const order = [...draftOrder, ...ARTIFACT_KINDS].filter((kind, index, all) => all.indexOf(kind) === index);
    return order
        .filter((kind) => labels[kind])
        .map((kind) => ({
            kind,
            label: labels[kind]!,
            status: hasPendingArtifactApproval(draft, kind) ? ('pending' as const) : created(kind) ? ('created' as const) : ('draft' as const)
        }));
};

/** The short entity name, its explanation and the guide it links to, per artifact kind. Used for the entity pill
 * in the preview header (and the outputs rail). */
export const ARTIFACT_INTRODUCTIONS: Record<AgentArtifactKind, { entity: string; text: string; guideUrl: string }> = {
    product: {
        guideUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360026668693-Create-a-customizable-product',
        entity: T._('customizable product', 'Admin'),
        text: T._(
            'Creating a {0}: the item your customers personalize, with its variants, the sides with their images and the print areas where designs can be placed.',
            'Admin'
        )
    },
    printingMethod: {
        guideUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360016836099-Printing-Methods',
        entity: T._('printing method', 'Admin'),
        text: T._('Creating a {0}: how designs are produced on the product and which customization tools and options your customers can use.', 'Admin')
    },
    pricing: {
        guideUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360015984779-How-to-create-pricing-rules',
        entity: T._('pricing rules', 'Admin'),
        text: T._('Creating {0}: how much customization costs, for example an extra charge for each side or print area your customers use.', 'Admin')
    }
};

export const artifactIntroduction = (kind: AgentArtifactKind) => {
    const { entity, text, guideUrl } = ARTIFACT_INTRODUCTIONS[kind];
    const [before, after] = text.split('{0}');
    return (
        <>
            {before}
            <strong>{entity}</strong>
            {after}{' '}
            <a href={guideUrl} target='_blank' rel='noopener noreferrer'>
                {T._('Learn more', 'Admin')}
            </a>
        </>
    );
};

interface AgentTaskPreviewProps extends AgentProductPreviewActions {
    draft: AgentDraft;
    disabled?: boolean;
    /** The artifact to show; defaults to the first available one. */
    artifact?: AgentArtifactKind;
    /** Rendered at the top right of the artifact, e.g. the expand/collapse control. */
    headerAction?: ReactNode;
    /** Latest image generation per side reference, from the conversation. */
    imageGenerations?: Record<string, AgentGeneratedImage>;
    /** The product side preview portals its action chips here, so they sit in the conversation under the preview. */
    actionsSlot?: HTMLElement | null;
    /** The read-only preview asks the agent to make edits via the conversation. */
    onRequestEdit?: (message: string) => void;
    /** The entity being built (e.g. "customizable product"): shown as a chip by the revision, opening an explanation. */
    entityLabel?: string;
    entityExplanation?: ReactNode;
    /** Whether the entity is still a draft or has been created (persisted) — shown as a chip in the header. */
    entityStatus?: AgentArtifact['status'] | 'unused';
    /** A surrounding result card already renders this label for historical snapshots. */
    showResultNumber?: boolean;
    /** A short revision pill shown in the header (e.g. "rev.3"), in place of the "Result N" label. */
    revisionLabel?: string;
    /** Render only the header row (the product heading with its pills); the body is hidden. Used by a collapsed
     * result snapshot, so the single header carries the collapse control and there is no second header above it. */
    collapsed?: boolean;
    /** A historical snapshot: the image cannot be edited directly, but the action chips still describe changes to the agent. */
    readOnly?: boolean;
}

const SurfaceDiagram = styled.div<{ $ratio: number; $circle: boolean; $background: string; $image?: string | null }>`
    width: min(100%, ${(p) => 340 * Math.min(1, p.$ratio)}px);
    aspect-ratio: ${(p) => p.$ratio};
    max-height: 340px;
    border: 1px solid #263238;
    border-radius: ${(p) => (p.$circle ? '50%' : '0')};
    background-color: ${(p) => p.$background};
    background-image: ${(p) => (p.$image ? `url(${JSON.stringify(p.$image)})` : 'none')};
    background-size: cover;
    margin: 16px auto;
`;

const ResizableSettingsSection = styled.section`
    > h5 {
        font-size: 13px;
        margin: 0 0 10px;
    }
    > dl {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 8px 16px;
        margin: 0;
        font-size: 13px;
        overflow-wrap: anywhere;
    }
    dt {
        color: #52636b;
    }
    dd {
        margin: 0;
    }
`;

const ResizableSurfaceSection = styled(Stack).attrs({ as: 'section' })`
    border-top: 1px solid ${(p) => p.theme.app.borderColor};
    padding-top: 16px;
    > h4 {
        margin: 0;
    }
`;

const ColorSample = styled.span<{ $color: string }>`
    display: inline-block;
    vertical-align: middle;
    width: 18px;
    height: 18px;
    border: 1px solid #84959e;
    border-radius: 3px;
    margin-right: 6px;
    background: ${(p) => p.$color};
`;

const ResizableProductPreview: FC<{ setup?: AgentResizableSetup | null }> = ({ setup }) => {
    const [unit, setUnit] = useState<Unit>(getStartingUnit);
    const backgrounds = useLazyLoadQuery<productBackgroundSectionQuery>(BackgroundPresetsQuery, {}).resizableBackgroundPresets ?? [];
    if (!setup?.sides?.length) return <EmptyState>{T._('Resizable surfaces have not been configured yet.', 'Admin')}</EmptyState>;
    const yesNo = (enabled?: boolean) => (enabled ? T._('Yes', 'Admin') : T._('No', 'Admin'));
    const missing = T._('Not set', 'Admin');
    const value = (number?: number | null, area = false) => formatResizableMeasurement(number, unit, config.culture, missing, area);
    const colorSample = (color?: string | null) =>
        color ? <ColorSample $color={color} title={color} aria-label={T._('Selected color', 'Admin')} role='img' /> : null;
    return (
        <Stack>
            <div>
                {setup.enableSyncSides !== false
                    ? T._('Sides share the same dimensions and settings.', 'Admin')
                    : T._('Each side has independent dimensions and settings.', 'Admin')}
            </div>
            <FormControl label={T._('Measure unit', 'Admin')}>
                <AdvancedSelect<Unit>
                    options={units}
                    value={unit}
                    onChange={(selected: Unit | null) => {
                        if (selected) setUnit(selected);
                    }}
                    isSearchable={false}
                />
            </FormControl>
            {setup.sides.map((side) => {
                const surface: AgentResizableSurface = setup.enableSyncSides !== false ? setup : (side.options ?? {});
                const m = surface.measurements;
                const material = backgrounds.find((preset) => preset?.presetID === surface.background?.presetId);
                const initial = `${value(m?.startWidth)} × ${value(m?.startHeight)}`;
                const ratio = m?.startWidth && m?.startHeight ? m.startWidth / m.startHeight : 1;
                const hasMaterial = surface.background?.type === 'material';
                const colorBackground = surface.background?.type === 'color';
                return (
                    <ResizableSurfaceSection key={side.referenceKey}>
                        <h4>{side.name}</h4>
                        <SurfaceDiagram
                            role='img'
                            aria-label={`${T._('Surface preview', 'Admin')}: ${side.name}, ${initial}`}
                            $ratio={Math.max(0.05, Math.min(20, ratio))}
                            $circle={surface.shape === 'circle' || surface.shape === 'ellipse'}
                            $background={colorBackground ? (surface.background?.color ?? '#FFFFFF') : '#FFFFFF'}
                            $image={hasMaterial ? (material?.imageUrl ?? surface.background?.imageUrl) : null}
                        />
                        <ResizableSettingsSection>
                            <h5>{T._('Shape', 'Admin')}</h5>
                            <dl>
                                <dt>{T._('Shape', 'Admin')}</dt>
                                <dd>
                                    {surface.shape === 'circle'
                                        ? T._('Circle', 'Admin')
                                        : surface.shape === 'ellipse'
                                          ? T._('Ellipse', 'Admin')
                                          : T._('Rectangle', 'Admin')}
                                </dd>
                            </dl>
                        </ResizableSettingsSection>
                        <ResizableSettingsSection>
                            <h5>{T._('Measurements', 'Admin')}</h5>
                            <dl>
                                <dt>{T._('Starting Width', 'Admin')}</dt>
                                <dd>{value(m?.startWidth)}</dd>
                                <dt>{T._('Starting Height', 'Admin')}</dt>
                                <dd>{value(m?.startHeight)}</dd>
                                <dt>{T._('Size limitation mode', 'Admin')}</dt>
                                <dd>
                                    {m?.constraintType === 'area'
                                        ? T._('Total Area Limit', 'Admin')
                                        : m?.constraintType === 'flexible'
                                          ? T._('Flexible Limits', 'Admin')
                                          : T._('Independent Width & Height Limits', 'Admin')}
                                </dd>
                                {m?.constraintType === 'area' ? (
                                    <>
                                        <dt>{T._('Minimum area', 'Admin')}</dt>
                                        <dd>{value(m.minArea, true)}</dd>
                                        <dt>{T._('Maximum area', 'Admin')}</dt>
                                        <dd>{value(m.maxArea, true)}</dd>
                                    </>
                                ) : m?.constraintType === 'flexible' ? (
                                    <>
                                        <dt>{T._('Minimum Measure', 'Admin')}</dt>
                                        <dd>{value(m.minWidth)}</dd>
                                        <dt>{T._('Maximum Measure', 'Admin')}</dt>
                                        <dd>{value(m.maxWidth)}</dd>
                                        <dt>{T._('Maximum - Other Side', 'Admin')}</dt>
                                        <dd>{value(m.maxSizeForSide)}</dd>
                                    </>
                                ) : (
                                    <>
                                        <dt>{T._('Width', 'Admin')}</dt>
                                        <dd>
                                            {m?.lockWidth ? (
                                                <>
                                                    {T._('Fixed', 'Admin')}: {value(m.startWidth)}
                                                </>
                                            ) : (
                                                <>
                                                    {T._('Resizable', 'Admin')}: {value(m?.minWidth)} – {value(m?.maxWidth)}
                                                </>
                                            )}
                                        </dd>
                                        <dt>{T._('Height', 'Admin')}</dt>
                                        <dd>
                                            {m?.lockHeight ? (
                                                <>
                                                    {T._('Fixed', 'Admin')}: {value(m.startHeight)}
                                                </>
                                            ) : (
                                                <>
                                                    {T._('Resizable', 'Admin')}: {value(m?.minHeight)} – {value(m?.maxHeight)}
                                                </>
                                            )}
                                        </dd>
                                    </>
                                )}
                                <dt>{T._('Keep aspect ratio', 'Admin')}</dt>
                                <dd>{yesNo(surface.keepAspectRatio || surface.shape === 'circle')}</dd>
                                <dt>{T._('Show width', 'Admin')}</dt>
                                <dd>{yesNo(surface.showWidthSize !== false)}</dd>
                                <dt>{T._('Show height', 'Admin')}</dt>
                                <dd>{yesNo(surface.showHeightSize !== false)}</dd>
                            </dl>
                        </ResizableSettingsSection>
                        <ResizableSettingsSection>
                            <h5>{T._('Step Adjustment', 'Admin')}</h5>
                            <dl>
                                <dt>{T._('Width increment', 'Admin')}</dt>
                                <dd>{m?.lockWidth ? T._('Fixed', 'Admin') : value(m?.stepWidth ?? 0.01)}</dd>
                                <dt>{T._('Height increment', 'Admin')}</dt>
                                <dd>{m?.lockHeight ? T._('Fixed', 'Admin') : value(m?.stepHeight ?? 0.01)}</dd>
                            </dl>
                        </ResizableSettingsSection>
                        <ResizableSettingsSection>
                            <h5>{T._('Background', 'Admin')}</h5>
                            <dl>
                                <dt>{T._('Background', 'Admin')}</dt>
                                <dd>
                                    {hasMaterial ? (
                                        surface.background?.presetId ? (
                                            getResizableMaterialName(surface.background.presetId, material?.name)
                                        ) : (
                                            T._('Custom image', 'Admin')
                                        )
                                    ) : colorBackground ? (
                                        <>
                                            {colorSample(surface.background?.color)}
                                            {T._('Solid color', 'Admin')}
                                        </>
                                    ) : (
                                        T._('None', 'Admin')
                                    )}
                                </dd>
                            </dl>
                        </ResizableSettingsSection>
                        <ResizableSettingsSection>
                            <h5>{T._('Border', 'Admin')}</h5>
                            <dl>
                                <dt>{T._('Border type', 'Admin')}</dt>
                                <dd>
                                    {surface.border ? (
                                        surface.border.type === 'mirrored' ? (
                                            T._('Mirror Wrap', 'Admin')
                                        ) : (
                                            <>
                                                {colorSample(surface.border.hexColor)}
                                                {T._('Solid Color', 'Admin')}
                                            </>
                                        )
                                    ) : (
                                        T._('Same as Background', 'Admin')
                                    )}
                                </dd>
                                {surface.border && (
                                    <>
                                        <dt>{T._('Border thickness', 'Admin')}</dt>
                                        <dd>{value(surface.border.borderSize)}</dd>
                                        <dt>{T._('Include Border in Print Files', 'Admin')}</dt>
                                        <dd>{yesNo(surface.border.includeBorderInPrintFiles)}</dd>
                                    </>
                                )}
                            </dl>
                        </ResizableSettingsSection>
                        <ResizableSettingsSection>
                            <h5>{T._('Multi-canvas', 'Admin')}</h5>
                            <dl>
                                <dt>{T._('Multi-canvas', 'Admin')}</dt>
                                <dd>{yesNo(!!surface.multiCanvas)}</dd>
                                {surface.multiCanvas && (
                                    <>
                                        <dt>{T._('Starting number of columns', 'Admin')}</dt>
                                        <dd>{surface.multiCanvas.startColumns ?? 1}</dd>
                                        <dt>{T._('Starting number of rows', 'Admin')}</dt>
                                        <dd>{surface.multiCanvas.startRows ?? 1}</dd>
                                        <dt>{T._('Min width', 'Admin')}</dt>
                                        <dd>{value(surface.multiCanvas.minColumnWidth)}</dd>
                                        <dt>{T._('Min height', 'Admin')}</dt>
                                        <dd>{value(surface.multiCanvas.minRowHeight)}</dd>
                                        <dt>{T._('Show split grid', 'Admin')}</dt>
                                        <dd>{yesNo(surface.showSplitGrid !== false)}</dd>
                                    </>
                                )}
                            </dl>
                        </ResizableSettingsSection>
                        <ResizableSettingsSection>
                            <h5>{T._('3D preview', 'Admin')}</h5>
                            <dl>
                                <dt>{T._('3D preview', 'Admin')}</dt>
                                <dd>{yesNo(surface.is3DAutoGenEnabled)}</dd>
                                {surface.is3DAutoGenEnabled && (
                                    <>
                                        <dt>{T._('Thickness', 'Admin')}</dt>
                                        <dd>{value(surface.depth ?? surface.border?.borderSize)}</dd>
                                    </>
                                )}
                                <dt>{T._('Augmented Reality', 'Admin')}</dt>
                                <dd>{yesNo(setup.arEnabled)}</dd>
                            </dl>
                        </ResizableSettingsSection>
                        {!!surface.presets?.length && (
                            <ResizableSettingsSection>
                                <h5>{T._('Preset options', 'Admin')}</h5>
                                <dl>
                                    {surface.presets.map((preset, index) => (
                                        <React.Fragment key={index}>
                                            <dt>{preset.name}</dt>
                                            <dd>
                                                {value(preset.width)} × {value(preset.height)}
                                            </dd>
                                        </React.Fragment>
                                    ))}
                                </dl>
                            </ResizableSettingsSection>
                        )}
                    </ResizableSurfaceSection>
                );
            })}
        </Stack>
    );
};

export const hasUsefulAgentPreview = (draft: AgentDraft): boolean => {
    const document = draft.document;
    return Boolean(
        document?.product?.resizable?.sides?.length ||
        document?.product?.variants?.some((variant) => variant.sides?.some((side) => side.image?.url || side.areas?.length)) ||
        document?.printingMethodSetup?.printingMethod?.name?.trim() ||
        document?.pricingSetup?.pricingGroup?.rules?.length
    );
};

export const AgentTaskPreview: FC<AgentTaskPreviewProps> = ({
    draft,
    disabled,
    artifact,
    headerAction,
    imageGenerations,
    actionsSlot,
    entityLabel,
    entityExplanation,
    entityStatus,
    showResultNumber = true,
    revisionLabel,
    collapsed = false,
    readOnly = false,
    ...actions
}) => {
    const [variantIndex, setVariantIndex] = useState(0);
    const [sideIndex, setSideIndex] = useState(0);
    const [entityOpen, setEntityOpen] = useState(false);
    const [sideView, setSideView] = useState<'single' | 'grid'>('single');
    const artifacts = getAgentArtifacts(draft);
    // Only one artifact at a time: the others are reached from the switcher above the artifact.
    const shown = artifacts.find((item) => item.kind === artifact)?.kind ?? artifacts[0]?.kind;
    // The entity pill (its type and explanation) and the status pill are derived from the draft, so they show even
    // when the caller (e.g. a historical result snapshot) does not pass them; an explicit prop still wins.
    const shownArtifact = artifacts.find((item) => item.kind === shown);
    const resolvedEntityLabel = entityLabel ?? (shown ? ARTIFACT_INTRODUCTIONS[shown].entity : undefined);
    const resolvedEntityExplanation = entityExplanation ?? (shown ? artifactIntroduction(shown) : undefined);
    const resolvedEntityStatus = entityStatus ?? shownArtifact?.status;
    const product = shown === 'product' ? draft.document?.product : undefined;
    const printingMethod = shown === 'printingMethod' ? draft.document?.printingMethodSetup?.printingMethod : undefined;
    const pricing = shown === 'pricing' ? draft.document?.pricingSetup?.pricingGroup : undefined;
    // Without the product heading the action gets its own row.
    const standaloneAction = headerAction && <HeaderActions $standalone>{headerAction}</HeaderActions>;
    if (!product && !printingMethod && !pricing) {
        return (
            <Preview>
                {standaloneAction}
                <Entities>
                    <EmptyState>{T._('The artifact of the current task will appear here when available.', 'Admin')}</EmptyState>
                </Entities>
            </Preview>
        );
    }

    const variants = product?.variants ?? [];
    // Indexes can outlive the list when the draft changes: fall back to the first item.
    const variant = variants[variantIndex] ?? variants[0];
    const sides = variant?.sides ?? [];
    const currentSideIndex = sideIndex < sides.length ? sideIndex : 0;
    const side = sides[currentSideIndex];
    const showSide = (index: number) => {
        setSideIndex(index);
        setSideView('single');
    };
    const areaCounts = variants.map((item) => (item.sides ?? []).reduce((total, current) => total + (current.areas?.length ?? 0), 0));
    const sameAreaCount = areaCounts.length > 0 && areaCounts.every((count) => count === areaCounts[0]);
    const areasMeta = sameAreaCount
        ? `${areaCounts[0]} ${areaCounts[0] === 1 ? T._('print area each', 'Admin') : T._('print areas each', 'Admin')}`
        : `${areaCounts.reduce((total, count) => total + count, 0)} ${T._('print areas', 'Admin')}`;

    // The header skeleton (name, revision, status, entity chip) is shared by every artifact kind. The name and the small
    // meta line under it are the only parts that differ.
    const entityName = product
        ? product.name || T._('Unnamed product', 'Admin')
        : printingMethod
          ? printingMethod.name || T._('Unnamed printing method', 'Admin')
          : pricing
            ? pricing.name || T._('Unnamed Advanced Pricing', 'Admin')
            : '';
    const entityMeta = product
        ? product.isResizable
            ? T._('Resizable product', 'Admin')
            : `${variants.length} ${variants.length === 1 ? T._('variant', 'Admin') : T._('variants', 'Admin')}${variants.length > 0 ? ` · ${areasMeta}` : ''}`
        : printingMethod
          ? T._('Printing method', 'Admin')
          : pricing
            ? T._('Advanced Pricing', 'Admin')
            : '';

    return (
        <Preview>
            <ProductHeading>
                <ProductTitleColumn>
                    <ProductTitleRow>
                        <ProductName>{entityName}</ProductName>
                        {entityMeta && <ProductMeta>{entityMeta}</ProductMeta>}
                    </ProductTitleRow>
                    {/* The revision sits under the name, on the left. */}
                    {revisionLabel ? (
                        <Revision>{revisionLabel}</Revision>
                    ) : (
                        showResultNumber &&
                        draft.resultNumber != null && (
                            <Revision>
                                {T._('Result', 'Admin')} {draft.resultNumber}
                            </Revision>
                        )
                    )}
                </ProductTitleColumn>
                {/* The status and entity pills sit on the right, and are hidden while the preview is collapsed. */}
                <HeaderActions>
                    {!collapsed && resolvedEntityStatus && (
                        <StatusChip $draft={resolvedEntityStatus !== 'created'} $unused={resolvedEntityStatus === 'unused'}>
                            {resolvedEntityStatus === 'draft'
                                ? T._('Draft', 'Admin')
                                : resolvedEntityStatus === 'pending'
                                  ? T._('Changes awaiting approval', 'Admin')
                                  : resolvedEntityStatus === 'unused'
                                    ? T._('Not used', 'Admin')
                                    : T._('Created', 'Admin')}
                        </StatusChip>
                    )}
                    {!collapsed && resolvedEntityLabel && resolvedEntityExplanation && (
                        <EntityChip type='button' $open={entityOpen} aria-expanded={entityOpen} onClick={() => setEntityOpen((open) => !open)}>
                            {resolvedEntityLabel}
                            <Icon>
                                <ExpandMoreIcon />
                            </Icon>
                        </EntityChip>
                    )}
                    {headerAction}
                </HeaderActions>
            </ProductHeading>
            {!collapsed && entityOpen && resolvedEntityExplanation && <EntityExplain>{resolvedEntityExplanation}</EntityExplain>}

            {product &&
                (collapsed ? null : product.isResizable ? (
                    <Entities>
                        <React.Suspense fallback={<SkeletonRow />}>
                            <ResizableProductPreview setup={product.resizable} />
                        </React.Suspense>
                    </Entities>
                ) : variants.length === 0 ? (
                    <Entities>
                        <EmptyState>{T._('No variants have been added yet.', 'Admin')}</EmptyState>
                    </Entities>
                ) : (
                    <>
                        {(variants.length > 1 || sides.length > 1) && (
                            <Selectors>
                                {variants.length > 1 && (
                                    <CollapsibleChips label={T._('Variants', 'Admin')}>
                                        {variants.map((item, index) => (
                                            <FilterChip
                                                key={item.entityReferenceKey ?? index}
                                                type='button'
                                                role='tab'
                                                aria-selected={item === variant}
                                                $selected={item === variant}
                                                onClick={() => {
                                                    setVariantIndex(index);
                                                    setSideIndex(0);
                                                }}
                                            >
                                                {item.hexCode && <Swatch $color={item.hexCode} />}
                                                {item.name || `${T._('Variant', 'Admin')} ${index + 1}`}
                                            </FilterChip>
                                        ))}
                                    </CollapsibleChips>
                                )}
                                {sides.length > 1 && (
                                    <SideRow>
                                        <Chips role='tablist' aria-label={T._('Sides', 'Admin')}>
                                            {sides.map((item, index) => (
                                                <FilterChip
                                                    key={item.entityReferenceKey ?? index}
                                                    type='button'
                                                    role='tab'
                                                    aria-selected={sideView === 'single' && item === side}
                                                    $selected={sideView === 'single' && item === side}
                                                    onClick={() => showSide(index)}
                                                >
                                                    {item.name || `${T._('Side', 'Admin')} ${index + 1}`}
                                                </FilterChip>
                                            ))}
                                        </Chips>
                                        {sides.length > 1 && (
                                            <ViewToggle role='group' aria-label={T._('Sides layout', 'Admin')}>
                                                <ZTooltip position='bottom' title={T._('One side', 'Admin')}>
                                                    <Chip
                                                        type='button'
                                                        aria-label={T._('One side', 'Admin')}
                                                        aria-pressed={sideView === 'single'}
                                                        $selected={sideView === 'single'}
                                                        $iconOnly
                                                        onClick={() => setSideView('single')}
                                                    >
                                                        <Icon>
                                                            <SingleSideIcon />
                                                        </Icon>
                                                    </Chip>
                                                </ZTooltip>
                                                <ZTooltip position='bottom' title={T._('All sides', 'Admin')}>
                                                    <Chip
                                                        type='button'
                                                        aria-label={T._('All sides', 'Admin')}
                                                        aria-pressed={sideView === 'grid'}
                                                        $selected={sideView === 'grid'}
                                                        $iconOnly
                                                        onClick={() => setSideView('grid')}
                                                    >
                                                        <Icon>
                                                            <GridViewIcon />
                                                        </Icon>
                                                    </Chip>
                                                </ZTooltip>
                                            </ViewToggle>
                                        )}
                                    </SideRow>
                                )}
                            </Selectors>
                        )}
                        {side && sideView === 'grid' && sides.length > 1 ? (
                            <SideGrid>
                                {sides.map((item, index) => (
                                    <AgentSideThumbnail
                                        key={item.entityReferenceKey ?? index}
                                        side={item}
                                        selected={item === side}
                                        imageGeneration={item.entityReferenceKey ? imageGenerations?.[item.entityReferenceKey] : undefined}
                                        onSelect={() => showSide(index)}
                                    />
                                ))}
                            </SideGrid>
                        ) : side ? (
                            <AgentProductSidePreview
                                key={`${variant.entityReferenceKey ?? variantIndex}:${side.entityReferenceKey ?? currentSideIndex}`}
                                variantRef={variant.entityReferenceKey ?? ''}
                                side={side}
                                revisionNumber={draft.drafts?.find((item) => item.kind === 'product')?.revisionNumber ?? draft.revisionNumber}
                                disabled={disabled}
                                readOnly={readOnly}
                                imageGeneration={side.entityReferenceKey ? imageGenerations?.[side.entityReferenceKey] : undefined}
                                zoomVariants={variants
                                    .map((item, index) => ({
                                        name: item.name || `${T._('Variant', 'Admin')} ${index + 1}`,
                                        images: (item.sides ?? []).flatMap((sideItem) => {
                                            const url = sideItem.image?.url;
                                            if (!url) return [];
                                            const w = sideItem.image?.widthPx ?? 0;
                                            const h = sideItem.image?.heightPx ?? 0;
                                            const areas =
                                                w > 0 && h > 0
                                                    ? (sideItem.areas ?? []).flatMap((area, areaIndex) => {
                                                          const raw = area.bounds?.raw;
                                                          if (!raw || !(raw.width > 0) || !(raw.height > 0)) return [];
                                                          return [
                                                              {
                                                                  left: raw.x / w + 0.5,
                                                                  top: raw.y / h + 0.5,
                                                                  width: raw.width / w,
                                                                  height: raw.height / h,
                                                                  label: area.name || area.label || `${T._('Print area', 'Admin')} ${areaIndex + 1}`
                                                              }
                                                          ];
                                                      })
                                                    : [];
                                            return [{ url, name: sideItem.name || T._('Product side', 'Admin'), areas }];
                                        })
                                    }))
                                    .filter((item) => item.images.length > 0)}
                                sideNavigation={
                                    sides.length > 1
                                        ? {
                                              position: currentSideIndex + 1,
                                              total: sides.length,
                                              onPrevious: () => setSideIndex((currentSideIndex - 1 + sides.length) % sides.length),
                                              onNext: () => setSideIndex((currentSideIndex + 1) % sides.length)
                                          }
                                        : undefined
                                }
                                actionsSlot={actionsSlot}
                                {...actions}
                            />
                        ) : (
                            <Entities>
                                <EmptyState>{T._('This variant has no sides yet.', 'Admin')}</EmptyState>
                            </Entities>
                        )}
                    </>
                ))}

            {!collapsed && (printingMethod || pricing) && (
                <Entities>
                    {printingMethod && <PrintingMethodPreview method={printingMethod} onRequestEdit={readOnly ? undefined : actions.onRequestEdit} />}
                    {pricing && <PricingPreview pricing={pricing} onRequestEdit={readOnly ? undefined : actions.onRequestEdit} />}
                </Entities>
            )}
        </Preview>
    );
};
